import getWidgetMode from '~/shared/utils/getWidgetMode';
import {
  NO_TRACK_PARAM,
  SINGLE_APP_PARAM,
} from '~/shared/constants/standaloneFlags';

/**
 * Query parameters that change how a standalone link behaves.
 *
 * - `bndl_wgt_single_app`: hide the widget header and footer so only the app is shown.
 * - `bndl_wgt_notrack`: disable all widget analytics and hide the cookie banner.
 *
 * Both are only honoured in standalone mode (`/-/{accountId}/s/...`). In iframe
 * mode the bootstrap forwards every host page parameter to the widget, so we
 * must not let a host page URL flip these switches.
 */
export type StandaloneFlags = {
  singleApp: boolean;
  noTrack: boolean;
};

const DISABLED_FLAGS = new Set(['0', 'false', 'no', 'off']);

const DEFAULT_FLAGS: StandaloneFlags = {
  singleApp: false,
  noTrack: false,
};

/**
 * A flag counts as enabled when it is present with any value other than an
 * explicit "off" value, so `?bndl_wgt_single_app`, `?bndl_wgt_single_app=1` and
 * `?bndl_wgt_single_app=true` all enable it.
 */
const isFlagEnabled = (params: URLSearchParams, key: string): boolean => {
  const value = params.get(key);
  if (value === null) return false;

  return !DISABLED_FLAGS.has(value.trim().toLowerCase());
};

const getStandaloneFlags = (
  search: string = window.location.search,
  pathname: string = window.location.pathname,
): StandaloneFlags => {
  if (getWidgetMode(pathname) !== 'standalone') return { ...DEFAULT_FLAGS };

  const params = new URLSearchParams(search);

  return {
    singleApp: isFlagEnabled(params, SINGLE_APP_PARAM),
    noTrack: isFlagEnabled(params, NO_TRACK_PARAM),
  };
};

export default getStandaloneFlags;
